import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Button,
  ImageList,
  ImageListItem,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Stack,
  Collapse,
} from '@mui/material';
import { BandRecommendationFormData, InstrumentWithPerformer } from '../../types/BandRecommendations';
import {
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  Link as LinkIcon,
  Audiotrack as AudiotrackIcon,
  Image as ImageIcon,
  Build as BuildIcon,
  AttachMoney as AttachMoneyIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  VideoLibrary as VideoLibraryIcon,
  Hotel as HotelIcon,
  DirectionsCar as DirectionsCarIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Newspaper as NewspaperIcon,
  EmojiEvents as EmojiEventsIcon,
  Festival as FestivalIcon,
  Radio as RadioIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
} from '@mui/icons-material';

interface ReviewSubmitProps {
  formData: BandRecommendationFormData;
  onSubmit: () => void;
  isEditMode?: boolean;
  onNavigate: (stepNumber: number) => void;
  formType?: 'recommendation' | 'staff_entry';
  steps: string[]; // The actual steps array from BandRecommendationForm so navigation is always correct
}

const ReviewSubmit: React.FC<ReviewSubmitProps> = ({
  formData,
  onSubmit,
  isEditMode,
  onNavigate = () => {},
  formType = 'recommendation',
  steps = [],
}) => {

  // Build step map dynamically from the actual steps array passed down.
  // This means Edit buttons are always correct regardless of form mode.
  const stepMap: Record<string, number> = {};
  steps.forEach((step, index) => { stepMap[step] = index; });

  const navigateTo = (stepName: string) => {
    const index = stepMap[stepName];
    if (index !== undefined) onNavigate(index);
  };

  // ─── Completeness calculations ───────────────────────────────────────────────

  const mediaCount = (formData.youtubeEmbeds?.length || 0)
    + (formData.spotifyEmbeds?.length || 0)
    + (formData.soundcloudEmbeds?.length || 0)
    + (formData.bandcampEmbeds?.length || 0)
    + (formData.musicLinks?.length || 0);

  const hasSocialMedia = Boolean(
    formData.instagramHandle || formData.twitterHandle ||
    formData.facebookPage || formData.tikTokHandle
  );

  const isMediaEmpty = mediaCount === 0 && !hasSocialMedia;

  const isImagesEmpty = !formData.mainImage &&
    (!formData.additionalImages || formData.additionalImages.length === 0);

  const isTechEmpty = !formData.technicalRider?.stageSize &&
    !formData.technicalRider?.audioRequirements &&
    !formData.technicalRider?.lightingRequirements &&
    (!formData.technicalRider?.additionalEquipment || formData.technicalRider.additionalEquipment.length === 0);

  // Financial: complete if fee is entered OR feeToBeConfirmed is ticked
  const isFinancialComplete = Boolean(
    formData.financialDetails?.feeToBeConfirmed ||
    (formData.financialDetails?.minimumFee && formData.financialDetails.minimumFee > 0)
  );

  const isFinancialEmpty = !isFinancialComplete &&
    !formData.financialDetails?.accommodationNeeded &&
    !formData.financialDetails?.transportationNeeded &&
    !formData.financialDetails?.logisticsToBeConfirmed;

  const bandInfoComplete = Boolean(formData.artistName && formData.about);
  const contactComplete = Boolean(formData.contactName && formData.contactEmail);

  // Overall completeness — Recommender only counted for recommendation mode
  const calculateOverallCompleteness = (): number => {
    const checks: boolean[] = [
      bandInfoComplete,
      contactComplete,
      Boolean(formData.availability?.from),
    ];
    if (formType === 'recommendation') {
      checks.push(Boolean(formData.recommenderName && formData.recommenderEmail));
    }
    return (checks.filter(Boolean).length / checks.length) * 100;
  };

  const overallCompleteness = calculateOverallCompleteness();

  // ─── Section wrapper ──────────────────────────────────────────────────────────

  const Section: React.FC<{
    title: string;
    stepName: string;
    children: React.ReactNode;
    completeness?: number;
    isRequired?: boolean;
    isEmpty?: boolean;
  }> = ({ title, stepName, children, completeness = 100, isRequired = false, isEmpty = false }) => {

    const color = completeness === 100 ? 'success' : completeness >= 50 ? 'warning' : 'error';
    const icon = completeness === 100
      ? <CheckIcon color="success" fontSize="small" />
      : <WarningIcon color={completeness >= 50 ? 'warning' : 'error'} fontSize="small" />;

    if (isEmpty && !isRequired) return null;

    return (
      <Card elevation={2} sx={{ mb: 2, opacity: isEmpty ? 0.6 : 1 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" color="primary">{title}</Typography>
                {icon}
              </Box>
              <LinearProgress
                variant="determinate"
                value={completeness}
                color={color}
                sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary">
                {completeness === 100 ? 'Complete' : `${Math.round(completeness)}% complete`}
              </Typography>
            </Box>

            {stepMap[stepName] !== undefined && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigateTo(stepName)}
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                Edit
              </Button>
            )}
          </Box>

          {isEmpty ? (
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                No information provided.
                {isRequired && ' This section is required before submitting.'}
              </Typography>
            </Alert>
          ) : children}
        </CardContent>
      </Card>
    );
  };

  const Row: React.FC<{
    icon: React.ReactElement;
    label: string;
    value?: string | React.ReactNode;
    divider?: boolean;
  }> = ({ icon, label, value, divider = false }) => (
    <ListItem divider={divider} sx={{ px: 0 }}>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            {React.cloneElement(icon, { sx: { color: 'primary.main', fontSize: 18 } })}
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}</Typography>
            <Typography variant="body1">{value || <em style={{ opacity: 0.5 }}>Not provided</em>}</Typography>
          </Box>
        }
      />
    </ListItem>
  );

  const totalMembers = Number(formData.maleMembers || 0) + Number(formData.femaleMembers || 0);

  // ─── Individual sections ──────────────────────────────────────────────────────

  const RecommenderSection = () => (
    <Section
      title="Page Owner Info"
      stepName="Page Owner Info"
      completeness={[formData.recommenderName, formData.recommenderEmail].filter(Boolean).length / 2 * 100}
      isEmpty={!formData.recommenderName && !formData.recommenderEmail}
    >
      <List sx={{ py: 0 }}>
        <Row icon={<PersonIcon />} label="Name" value={formData.recommenderName} divider />
        <Row icon={<EmailIcon />} label="Email" value={formData.recommenderEmail} divider />
      </List>
    </Section>
  );

  const BandInfoSection = () => (
    <Section
      title="Band / Artist Information"
      stepName="Band/Artist Information"
      completeness={[formData.artistName, formData.about].filter(Boolean).length / 2 * 100}
      isRequired
      isEmpty={!formData.artistName}
    >
      <List sx={{ py: 0 }}>
        <Row icon={<MusicNoteIcon />} label="Name" value={formData.artistName} divider />

        {totalMembers > 0 && (
          <ListItem divider sx={{ px: 0 }}>
            <ListItemText primary={
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <PeopleIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>Members</Typography>
                <Chip label={`${formData.maleMembers} Male`}   color="primary"   size="small" />
                <Chip label={`${formData.femaleMembers} Female`} color="secondary" size="small" />
                <Chip label={`${totalMembers} Total`} variant="outlined" size="small" />
              </Box>
            } />
          </ListItem>
        )}

        {formData.activityStatus && (
          <Row
            icon={<CheckCircleIcon />}
            label="Status"
            value={
              formData.activityStatusNote
                ? `${formData.activityStatus} — ${formData.activityStatusNote}`
                : formData.activityStatus
            }
            divider
          />
        )}

        {formData.about && (
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary={
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>About</Typography>
                <Typography variant="body2">
                  {formData.about.length > 300
                    ? `${formData.about.substring(0, 300)}…`
                    : formData.about}
                </Typography>
              </Box>
            } />
          </ListItem>
        )}

        {formData.performanceLink && (
          <ListItem sx={{ px: 0 }}>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => window.open(formData.performanceLink, '_blank')}
            >
              View Performance Link
            </Button>
          </ListItem>
        )}
      </List>
    </Section>
  );

  const InstrumentationSection = () => (
    <Section
      title="Instrumentation"
      stepName="Band/Artist Information"
      isEmpty={!formData.instrumentation || formData.instrumentation.length === 0}
    >
      <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
        {formData.instrumentation?.map((inst: InstrumentWithPerformer, i) => (
          <Chip
            key={i}
            label={
              inst.performer?.name
                ? `${inst.instrumentName}${inst.withEffects ? ' (FX)' : ''} — ${inst.performer.name}`
                : `${inst.instrumentName}${inst.withEffects ? ' (FX)' : ''}`
            }
            color={inst.withEffects ? 'primary' : 'default'}
            variant="outlined"
            icon={<MusicNoteIcon />}
          />
        ))}
      </Box>
    </Section>
  );

  const ContactSection = () => (
    <Section
      title="Booking Contact"
      stepName="Contact Information"
      completeness={[formData.contactName, formData.contactEmail].filter(Boolean).length / 2 * 100}
      isRequired
      isEmpty={!formData.contactName && !formData.contactEmail}
    >
      <List sx={{ py: 0 }}>
        <Row icon={<PersonIcon />} label="Name"  value={formData.contactName}  divider />
        <Row icon={<EmailIcon />}  label="Email" value={formData.contactEmail} divider />
        <Row icon={<PhoneIcon />}  label="Phone" value={formData.contactPhone || '—'} />
      </List>
      <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
        <Typography variant="caption">
          This contact information is private and will not appear on the public band page.
        </Typography>
      </Alert>
    </Section>
  );

  const AvailabilitySection = () => {
    const isGenerallyAvailable = formData.financialDetails?.logisticsToBeConfirmed === undefined
      ? false
      : false; // Check availability directly
    
    // Detect if "Generally Available" was selected by checking the date range
    const currentYear = new Date().getFullYear();
    const fromStr = formData.availability?.from
      ? new Date(formData.availability.from).getFullYear()
      : null;
    const toStr = formData.availability?.to
      ? new Date(formData.availability.to).getFullYear()
      : null;
    const looksGenerallyAvailable = fromStr === currentYear && toStr && toStr >= currentYear + 1;

    return (
      <Section
        title="Availability"
        stepName="Availability"
        isRequired
        isEmpty={!formData.availability?.from}
      >
        {looksGenerallyAvailable ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            <Typography variant="body2">
              <strong>Generally Available</strong> — the band is broadly available for bookings.
              Exact dates will be confirmed with the booking contact.
            </Typography>
          </Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <CalendarTodayIcon color="primary" sx={{ mb: 0.5 }} />
                <Typography variant="overline" display="block">From</Typography>
                <Typography variant="h6">
                  {formData.availability?.from
                    ? new Date(formData.availability.from).toLocaleDateString()
                    : '—'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <CalendarTodayIcon color="primary" sx={{ mb: 0.5 }} />
                <Typography variant="overline" display="block">Until</Typography>
                <Typography variant="h6">
                  {formData.availability?.to
                    ? new Date(formData.availability.to).toLocaleDateString()
                    : '—'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Section>
    );
  };

  const MediaSection = () => (
    <Section
      title="Media"
      stepName="Media Links"
      isEmpty={isMediaEmpty}
      completeness={mediaCount > 0 ? 100 : 0}
    >
      {mediaCount > 0 && (
        <Box sx={{ mb: hasSocialMedia ? 2 : 0 }}>
          <Typography variant="subtitle2" gutterBottom>Embeds added:</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {formData.youtubeEmbeds?.map((_, i) =>
              <Chip key={`yt-${i}`} label={`YouTube ${i + 1}`} icon={<VideoLibraryIcon />} color="primary" variant="outlined" size="small" />
            )}
            {formData.spotifyEmbeds?.map((_, i) =>
              <Chip key={`sp-${i}`} label={`Spotify ${i + 1}`} icon={<AudiotrackIcon />} color="secondary" variant="outlined" size="small" />
            )}
            {formData.soundcloudEmbeds?.map((_, i) =>
              <Chip key={`sc-${i}`} label={`SoundCloud ${i + 1}`} icon={<AudiotrackIcon />} color="secondary" variant="outlined" size="small" />
            )}
            {formData.bandcampEmbeds?.map((_, i) =>
              <Chip key={`bc-${i}`} label={`Bandcamp ${i + 1}`} icon={<LinkIcon />} variant="outlined" size="small" />
            )}
            {formData.musicLinks?.map((link, i) =>
              <Chip
                key={`ml-${i}`}
                label={link.label || link.url}
                icon={<LinkIcon />}
                variant="outlined"
                size="small"
                color={link.url.toLowerCase().endsWith('.mp3') ? 'warning' : 'default'}
              />
            )}
          </Box>
        </Box>
      )}

      {hasSocialMedia && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Social media:</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {formData.instagramHandle && (
              <Chip icon={<InstagramIcon />} label={`@${formData.instagramHandle}`} size="small"
                onClick={() => window.open(`https://instagram.com/${formData.instagramHandle}`, '_blank')} />
            )}
            {formData.twitterHandle && (
              <Chip icon={<TwitterIcon />} label={`@${formData.twitterHandle}`} size="small"
                onClick={() => window.open(`https://twitter.com/${formData.twitterHandle}`, '_blank')} />
            )}
            {formData.facebookPage && (
              <Chip icon={<FacebookIcon />} label="Facebook" size="small"
                onClick={() => window.open(`https://facebook.com/${formData.facebookPage}`, '_blank')} />
            )}
            {formData.tikTokHandle && (
              <Chip label={`@${formData.tikTokHandle} (TikTok)`} size="small"
                onClick={() => window.open(`https://tiktok.com/@${formData.tikTokHandle}`, '_blank')} />
            )}
          </Box>
        </Box>
      )}
    </Section>
  );

  const ImagesSection = () => (
    <Section
      title="Images"
      stepName="Additional Media"
      isEmpty={isImagesEmpty}
      completeness={formData.mainImage ? 100 : 0}
    >
      {formData.mainImage && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Main Image:</Typography>
          <Box
            component="img"
            src={formData.mainImage}
            alt="Main band image"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none';
            }}
            sx={{
              width: '100%',
              maxHeight: 280,
              objectFit: 'cover',
              borderRadius: 1,
              display: 'block',
            }}
          />
        </Box>
      )}

      {formData.additionalImages && formData.additionalImages.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Additional Images ({formData.additionalImages.length}):
          </Typography>
          <ImageList cols={3} gap={8}>
            {formData.additionalImages.map((url, i) => (
              <ImageListItem key={i}>
                <Box
                  component="img"
                  src={url}
                  alt={`Additional ${i + 1}`}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = 'none';
                  }}
                  sx={{ borderRadius: 0.5, width: '100%', height: 100, objectFit: 'cover' }}
                  loading="lazy"
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}
    </Section>
  );

  const CategorisationSection = () => {
    const hasGenres = formData.genres && formData.genres.length > 0;
    const hasSimilar = formData.similarArtists && formData.similarArtists.length > 0;
    const hasNotes = Boolean(formData.recommendationNotes);

    return (
      <Section
        title="Categorisation"
        stepName="Categorisation"
        isEmpty={!hasGenres && !hasSimilar && !hasNotes}
      >
        {hasGenres && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Genres:</Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.genres.map((g, i) => <Chip key={i} label={g} color="primary" variant="outlined" size="small" />)}
            </Box>
          </Box>
        )}
        {hasSimilar && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Similar Artists:</Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.similarArtists!.map((a, i) => (
                <Chip key={i} label={a} avatar={<Avatar><PersonIcon /></Avatar>} variant="outlined" size="small" />
              ))}
            </Box>
          </Box>
        )}
        {hasNotes && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Additional Notes:</Typography>
            <Typography variant="body2">{formData.recommendationNotes}</Typography>
          </Box>
        )}
      </Section>
    );
  };

  const TechnicalSection = () => (
    <Section
      title="Technical Requirements"
      stepName="Technical Requirements"
      isEmpty={isTechEmpty}
      completeness={[
        formData.technicalRider?.stageSize,
        formData.technicalRider?.audioRequirements,
        formData.technicalRider?.lightingRequirements,
      ].filter(Boolean).length / 3 * 100}
    >
      <List sx={{ py: 0 }}>
        {formData.technicalRider?.stageSize && (
          <Row icon={<SettingsIcon />} label="Stage Size" value={formData.technicalRider.stageSize} divider />
        )}
        {formData.technicalRider?.audioRequirements && (
          <Row icon={<AudiotrackIcon />} label="Audio" value={formData.technicalRider.audioRequirements} divider />
        )}
        {formData.technicalRider?.lightingRequirements && (
          <Row icon={<BuildIcon />} label="Lighting" value={formData.technicalRider.lightingRequirements} divider />
        )}
        {formData.technicalRider?.additionalEquipment && formData.technicalRider.additionalEquipment.length > 0 && (
          <ListItem sx={{ px: 0 }}>
            <ListItemText primary={
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>Additional Equipment:</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {formData.technicalRider.additionalEquipment.map((item, i) => (
                    <Chip key={i} label={item} size="small" />
                  ))}
                </Box>
              </Box>
            } />
          </ListItem>
        )}
      </List>
    </Section>
  );

  const FinancialSection = () => {
    const fd = formData.financialDetails;
    const feeConfirmed    = fd?.feeToBeConfirmed;
    const logisticsTBC    = fd?.logisticsToBeConfirmed;
    const hasFee          = fd?.minimumFee && fd.minimumFee > 0;
    const currency        = fd?.preferredCurrency || 'EUR';

    return (
      <Section
        title="Financial Information"
        stepName="Financial Information"
        isEmpty={isFinancialEmpty}
        completeness={isFinancialComplete ? 100 : 50}
      >
        {/* Fee */}
        {feeConfirmed ? (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Fee to be confirmed directly with booking contact.
          </Alert>
        ) : hasFee ? (
          <Row icon={<AttachMoneyIcon />} label="Minimum Fee" value={`${fd!.minimumFee} ${currency}`} divider />
        ) : null}

        {/* Logistics */}
        {logisticsTBC ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            Accommodation and transport to be confirmed with booking contact.
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Logistics:</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<HotelIcon />}
                label="Accommodation"
                color={fd?.accommodationNeeded ? 'primary' : 'default'}
                variant={fd?.accommodationNeeded ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip
                icon={<DirectionsCarIcon />}
                label="Transport"
                color={fd?.transportationNeeded ? 'primary' : 'default'}
                variant={fd?.transportationNeeded ? 'filled' : 'outlined'}
                size="small"
              />
            </Stack>

            {fd?.accommodationNeeded && fd.accommodationDetails && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {fd.accommodationDetails.type && `Type: ${fd.accommodationDetails.type}`}
                  {fd.accommodationDetails.nights && ` • ${fd.accommodationDetails.nights} night(s)`}
                  {fd.accommodationDetails.specialRequirements && ` • ${fd.accommodationDetails.specialRequirements}`}
                </Typography>
              </Box>
            )}

            {fd?.transportationNeeded && fd.transportDetails && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {fd.transportDetails.type && `Transport: ${fd.transportDetails.type}`}
                  {fd.transportDetails.distance && ` • Max ${fd.transportDetails.distance}km`}
                  {fd.transportDetails.returnNeeded && ' • Return needed'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Section>
    );
  };

  const PressAwardsSection = () => {
    const entries = formData.externalValidation || [];
    if (entries.length === 0) return null;

    const typeConfig: Record<string, { label: string; icon: React.ReactElement; color: 'primary' | 'warning' | 'secondary' | 'info' | 'default' }> = {
      press_review:        { label: 'Press Review',       icon: <NewspaperIcon />,   color: 'primary' },
      award:               { label: 'Award',              icon: <EmojiEventsIcon />, color: 'warning' },
      festival_appearance: { label: 'Festival',           icon: <FestivalIcon />,    color: 'secondary' },
      radio_airplay:       { label: 'Radio / Media',      icon: <RadioIcon />,       color: 'info' },
      other:               { label: 'Recognition',        icon: <VerifiedIcon />,    color: 'default' },
    };

    return (
      <Section
        title="Press & Awards"
        stepName="Press & Awards"
        isEmpty={entries.length === 0}
        completeness={100}
      >
        <Stack spacing={1.5}>
          {entries.map((v, i) => {
            const cfg = typeConfig[v.type] || typeConfig.other;
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Chip
                  icon={cfg.icon}
                  label={cfg.label}
                  color={cfg.color}
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
                {v.featured && (
                  <Chip icon={<StarIcon />} label="Featured" color="warning" size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{v.source}</Typography>
                  {v.quote && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {v.quote.length > 100 ? `${v.quote.substring(0, 100)}…` : v.quote}
                    </Typography>
                  )}
                  {v.date && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(v.date).toLocaleDateString('en-IE', { year: 'numeric', month: 'long' })}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Section>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom color="primary">
          {isEditMode ? 'Review Your Changes' : 'Review Before Submitting'}
        </Typography>

        <Box sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Required sections complete</Typography>
            <Typography variant="body2" color="text.secondary">{Math.round(overallCompleteness)}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallCompleteness}
            color={overallCompleteness === 100 ? 'success' : overallCompleteness >= 50 ? 'warning' : 'error'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {overallCompleteness < 100 && (
          <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
            Some required sections are incomplete. Please review below before submitting.
          </Alert>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="primary" /> Essential Information
          </Typography>

          {/* Only show Page Owner section when data is present (admin-entered) */}
          {(formData.recommenderName || formData.recommenderEmail) && <RecommenderSection />}

          <BandInfoSection />
          <InstrumentationSection />
          <ContactSection />
          <AvailabilitySection />
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MusicNoteIcon color="primary" /> Media & Content
          </Typography>

          <ImagesSection />
          <MediaSection />
          <CategorisationSection />
          <PressAwardsSection />
        </Grid>

        {/* Full width — professional details */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" /> Professional Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TechnicalSection /></Grid>
            <Grid item xs={12} md={6}><FinancialSection /></Grid>
          </Grid>
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {overallCompleteness < 100 && (
              <Alert severity="error" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                <strong>Required information missing.</strong> Please complete all required sections before submitting.
              </Alert>
            )}

            {!isEditMode && formType === 'recommendation' && (
              <Alert severity="info" sx={{ mb: 3, maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
                <strong>How submission works:</strong> Your band page is currently saved as a private draft — only you can see it.
                When you hit Submit, it will be sent to the IMC platform administrators for review.
                Please allow <strong>up to 24 hours</strong> on weekdays or <strong>48 hours over weekends</strong>.
                Once approved, your page will be live and visible to everyone on imc.show.
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onSubmit}
              disabled={overallCompleteness < 100}
              sx={{ minWidth: 250, py: 1.5, fontSize: '1.1rem', boxShadow: 3 }}
            >
              {isEditMode ? 'Save Changes' : formType === 'staff_entry' ? 'Add Band to Database' : 'Submit for Review'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReviewSubmit;